import { PrismaClient as BasePrismaClient } from '@prisma/client';
import { writeFileSync, mkdirSync, existsSync } from 'fs';
import { join } from 'path';

// Define our model name type
export type ModelName = string;

// Type for the Prisma client with our custom methods
type PrismaClient = BasePrismaClient & {
  $connect: () => Promise<void>;
  $disconnect: () => Promise<void>;
};

interface GenerateCRUDOptions {
  outputDir: string;
  models?: ModelName[];
  exclude?: ModelName[];
  auth?: {
    required?: boolean;
    userIdField?: string;
  };
}

export class CRUDGenerator {
  constructor(private prisma: PrismaClient) {}

  private getModelNames(): ModelName[] {
    // This is a simplified implementation
    // In a real implementation, you'd parse the Prisma schema
    return [];
  }

  private getModelFields(model: string): string[] {
    // This is a simplified version - in a real implementation, you'd want to
    // introspect the Prisma schema to get the actual fields
    const modelName = model as keyof typeof this.prisma;
    const modelClient = this.prisma[modelName] as any;
    
    if (!modelClient) return [];
    
    // Get the first item to infer fields (simplified for example)
    // In a real implementation, you'd parse the Prisma schema
    return ['id', 'createdAt', 'updatedAt'];
  }

  private generateRouteContent(model: string, fields: string[]): string {
    const modelLower = model.toLowerCase();
    const modelId = `${modelLower}Id`;

    return `// Generated by @api-buddy/prisma
import { NextResponse } from 'next/server';
import { prisma } from '@/lib/prisma';

// GET /api/${modelLower}
export async function GET() {
  try {
    const items = await prisma.${modelLower}.findMany();
    return NextResponse.json(items);
  } catch (error) {
    return NextResponse.json(
      { error: 'Failed to fetch ${modelLower}s' },
      { status: 500 }
    );
  }
}

// POST /api/${modelLower}
export async function POST(request: Request) {
  try {
    const data = await request.json();
    const item = await prisma.${modelLower}.create({ data });
    return NextResponse.json(item, { status: 201 });
  } catch (error) {
    return NextResponse.json(
      { error: 'Failed to create ${modelLower}' },
      { status: 500 }
    );
  }
}
`;
  }

  async generateCRUDRoutes(options: GenerateCRUDOptions) {
    const { outputDir, models, exclude = [], auth } = options;
    
    // Create output directory if it doesn't exist
    if (!existsSync(outputDir)) {
      mkdirSync(outputDir, { recursive: true });
    }

    // Determine which models to generate routes for
    const targetModels = models || this.getModelNames();
    const filteredModels = targetModels.filter(
      (model) => !exclude.includes(model)
    );

    // Generate route files
    for (const model of filteredModels) {
      const modelName = String(model);
      const fields = this.getModelFields(modelName);
      const content = this.generateRouteContent(modelName, fields);
      const routePath = join(outputDir, modelName.toLowerCase(), 'route.ts');
      
      // Create model directory if it doesn't exist
      const modelDir = join(outputDir, modelName.toLowerCase());
      if (!existsSync(modelDir)) {
        mkdirSync(modelDir, { recursive: true });
      }
      
      writeFileSync(routePath, content);
      console.log(`✅ Generated CRUD routes for ${model} at ${routePath}`);
    }
  }
}

// Utility function to use the generator
export async function generateCRUDRoutes(
  prisma: PrismaClient,
  options: GenerateCRUDOptions
) {
  const generator = new CRUDGenerator(prisma);
  return generator.generateCRUDRoutes(options);
}
